import { Blueprint, BuildCmd, ConfigCmd, FixedAngle, Item, Shape } from "dsabp-js"





import { BpRenderer } from "./BpRenderer.js"
import { Canvas, CanvasRenderingContext2D, Image, createCanvas, loadImage } from "./canvas.js"
import { MAX_SIZE, customImagePathHandler, imagesPath } from "./index.js"
import { isNode, rendererResolvers } from "./main.js"
import { connectedTileTypes, tileRendererResolver } from "./renderers/Tile.js"
import { Hull, Renderer, RendererResolverParams, TileFace } from "./types.js"

export function clearCanvas(c: Canvas, ctx: CanvasRenderingContext2D, width: number, height: number) {
	c.width = width
	c.height = height
	ctx.clearRect(0, 0, width, height)
}

export function putInCanvas(c: Canvas, ctx: CanvasRenderingContext2D, img: Image | Canvas) {
	clearCanvas(c, ctx, img.width, img.height)
	ctx.drawImage(img, 0, 0)
}

export function drawRotatedImage(ctx: CanvasRenderingContext2D, image: Canvas | Image, angle: number, x: number, y: number, originX: number, originY: number, dw = image.width, dh = image.height, sx: number = 0, sy: number = 0, sw: number = dw, sh: number = dh) {
	ctx.save()
	ctx.translate(x, y)
	ctx.rotate(angle * Math.PI / 180)
	ctx.drawImage(image, sx, sy, sw, sh, -originX, -originY, dw, dh)
	ctx.restore()
}

export function drawRotatedImages(ctx: CanvasRenderingContext2D, image: Canvas | Image, opts: [angle: number, x: number, y: number][], originX: number, originY: number, dw = image.width, dh = image.height) {
	for (const opt of opts)
		drawRotatedImage(ctx, image, opt[0], opt[1], opt[2], originX, originY, dw, dh)
}

export function toImage(canvas: Canvas) {
	return loadImage(isNode ? canvas.toBuffer("image/png") : canvas.toDataURL())
}

function getExtname(path) {
	if (isNode) return extname(path)
	const lastSlashIndex = path.lastIndexOf("/")
	const lastDotIndex = path.lastIndexOf(".")
	return lastDotIndex > lastSlashIndex ? path.slice(lastDotIndex) : ""
}

// GET IMAGE
export async function getImage(src: string, crossOrigin?: "anonymous", isAbs?: boolean) {
	if (!src) return null

	if (customImagePathHandler) {
		const newSrc = customImagePathHandler(src)
		if (newSrc) {
			src = newSrc
			isAbs = true
		}
	}

	isAbs = isAbs || src.startsWith("http")

	const extToAppend = (getExtname(src) == "" ? ".png" : ""),
		path = isAbs ? src : (isNode ? join(imagesPath, src + extToAppend) : (imagesPath + src + extToAppend))

	let img: Image
	if (isNode) {
		img = isAbs
			? await loadImage(path)
			: isNode && await access(path).then(() => true).catch(() => false)
				? await loadImage(path)
				: undefined
		if (!isAbs && !img?.naturalWidth) {
			// image doesn't exist locally, try fetch
			const res = await fetch("https://test.drednot.io/img/" + src + extToAppend)
			await mkdir(dirname(path), { recursive: true })
			const stream = createWriteStream(path, { flags: "w+" })
			await finished(Readable.fromWeb(res.body as any).pipe(stream))
			return getImage(path, null, true)
		}
	} else {
		img = await loadImage(path, { crossOrigin } as any).catch(() => undefined)
			?? await loadImage(`${location.origin}/p?https://test.drednot.io/img/` + src + extToAppend, { crossOrigin } as any).catch(() => undefined)
	}

	return img
}

// GET RENDERER
export function getRenderer({ main, item, shape, config, other }: RendererResolverParams) {
	if (!item.buildInfo?.[0])
		throw "Rendering not supported: " + item.enumName

	if (!config)
		config = Object.assign(new ConfigCmd(), ConfigCmd.defaults)

	let renderer: Renderer

	for (const resolve of rendererResolvers)
		if ((renderer = resolve({ main, toRel: main.toRel.bind(main), item, shape, config, other })))
			break

	if (!renderer) {
		renderer = {
			discriminator: `${item.id}${shape ? "_" + shape.enumValue : ""}`,
			render: async () => [{ image: await getImage(item.buildInfo[0].image) }]
		}
	}

	return renderer
}

// GET HULL RENDERER
export function getHullRenderer(main: BpRenderer, hull: Hull) {
	return tileRendererResolver({
		main,
		item: Item.NULL,
		other: { hull },
		shape: null
	})
}

// GET TINTED IMAGE
export const tintCanvas = createCanvas(0, 0)
export const tintCtx = tintCanvas.getContext("2d")
export async function getTinted(img: Image | Promise<Image>, color: string, alpha?: number) {
	img = await img
	tintCanvas.width = img.width
	tintCanvas.height = img.height
	tintCtx.clearRect(0, 0, img.width, img.height)

	if (alpha != null)
		tintCtx.globalAlpha = alpha
	tintCtx.drawImage(img, 0, 0)
	tintCtx.fillStyle = color
	tintCtx.globalCompositeOperation = "multiply"
	tintCtx.fillRect(0, 0, img.width, img.height)
	tintCtx.globalCompositeOperation = "destination-in"
	tintCtx.drawImage(img, 0, 0)
	return toImage(tintCanvas)
}

// GET ROTATED IMAGE
export const rotateCanvas = createCanvas(0, 0)
export const rotateCtx = rotateCanvas.getContext("2d")
export async function getRotated(img: Image | Promise<Image>, angle: number, resize?: boolean) {
	img = await img
	angle *= (Math.PI / 180)

	let width: number, height: number
	if (resize) {
		const sin = Math.abs(Math.sin(angle)), cos = Math.abs(Math.cos(angle))
		width = img.height * sin + img.width * cos
		height = img.height * cos + img.width * sin
	} else {
		width = img.width
		height = img.height
	}
	rotateCanvas.width = width
	rotateCanvas.height = height

	rotateCtx.save()
	rotateCtx.clearRect(0, 0, width, height)
	rotateCtx.translate(width / 2, height / 2)
	rotateCtx.rotate(angle)
	rotateCtx.drawImage(img, -img.width / 2, -img.height / 2)
	rotateCtx.restore()
	return toImage(rotateCanvas)
}

// GET SHIP BACKGROUND DATA - NOTE: Not fully tested, wall checks are broken
export async function getShipBackgroundData(renderer: BpRenderer, bp?: Blueprint, width = bp.width, height = bp.height) {
	const checks: {
		noWall: boolean
		noWallSides: Set<"top" | "right" | "bottom" | "left">
		isMaxSize: boolean
	} = {
		noWall: true,
		noWallSides: new Set(),
		isMaxSize: width == MAX_SIZE && height == MAX_SIZE
	}

	if (bp) for (const cmd of bp.commands) {
		if (cmd instanceof BuildCmd && cmd.item) {
			const { item, x, y } = cmd

			if (!checks.isMaxSize) {
				const isHull = cmd.item.isBuildable && cmd.item.buildInfo?.[0].require_blocks?.[0].block.includes("HULL")
				if (isHull) {
					checks.noWall = false
				} else if (cmd.item.isBuildable) {
					if (x == 0)
						checks.noWallSides.add("left")
					else if (x == bp.width - 1)
						checks.noWallSides.add("right")
					else if (y == 0)
						checks.noWallSides.add("bottom")
					else if (y == bp.height - 1)
						checks.noWallSides.add("top")
				}
			}

			if (renderer.knownObjectTypes.has(cmd.item)) {
				if (cmd.bits) {
					let bitI = 0
					for (const bit of cmd.bits) {
						if (bit) {
							renderer.knownObjects[x + bitI] ??= []
							renderer.knownObjects[x + bitI][y] = item
						}
						bitI++
					}
				} else {
					renderer.knownObjects[x] ??= []
					renderer.knownObjects[x][y] = item
				}
			}
		}
	}

	const imgPaint = await getPaintImage(),
		imgHullC = (await getHullRenderer(renderer, "HULL_CORNER").render())[0].image,
		imgHullH = (await getHullRenderer(renderer, "HULL_H").render())[0].image,
		imgHullV = (await getHullRenderer(renderer, "HULL_V").render())[0].image

	const result = {
		tiles: [] as { type: "paint" | "wallC" | "wallH" | "wallV", image: Image }[][],
		checks
	}

	for (let x = 0; x < width; x++) {
		for (let y = 0; y < height; y++) {
			let img: Image
			if (!checks.noWallSides.has("left") && x == 0) {
				if (!checks.noWallSides.has("bottom") && y == 0)
					img = imgHullC
				else if (!checks.noWallSides.has("top") && y == height - 1)
					img = imgHullC
				else
					img = imgHullV
			}
			if (!img && !checks.noWallSides.has("right") && x == width - 1) {
				if (!checks.noWallSides.has("bottom") && y == 0)
					img = imgHullC
				else if (!checks.noWallSides.has("top") && y == height - 1)
					img = imgHullC
				else
					img = imgHullV
			}
			if (!img && !checks.noWallSides.has("bottom") && y == 0)
				img = imgHullH
			if (!img && !checks.noWallSides.has("top") && y == height - 1)
				img = imgHullH
			result.tiles[x] ??= []
			result.tiles[x][y] = {
				type: img ? (img == imgHullH ? "wallH" : (img == imgHullV ? "wallV" : "wallC")) : "paint",
				image: img ?? imgPaint
			}
		}
	}

	return result
}

export function getPaintImage() {
	return getTinted(getImage("bg_ship"), "rgb(187,187,187)")
}

// GET DRAW ITEM DATA
export async function getDrawItemData(main: BpRenderer, item: Item, x: number, y: number, shape?: Shape, config?: Partial<ConfigCmd>, hullDirection?: FixedAngle, other?: Record<string, any>) {
	const squareSize = main.squareSize

	const resolverParams: RendererResolverParams = { main, item, shape, config, other: { hullDirection } }
	if (connectedTileTypes.has(item)) {
		let connectedTileFaces = 0
		if (main.getKnownObject(x, y + 1) == item)
			connectedTileFaces |= TileFace.Top
		if (main.getKnownObject(x + 1, y) == item)
			connectedTileFaces |= TileFace.Right
		if (main.getKnownObject(x, y - 1) == item)
			connectedTileFaces |= TileFace.Bottom
		if (main.getKnownObject(x - 1, y) == item)
			connectedTileFaces |= TileFace.Left
		resolverParams.other.connectedTileFaces = connectedTileFaces
	}

	if (other) for (const key in other)
		resolverParams.other[key] = other[key]

	const renderer = getRenderer(resolverParams)
	const discr = renderer.discriminator

	let renderResults = main.renderCache[discr]
	if (!renderResults) {
		renderResults = await renderer.render()
		main.renderCache[discr] = renderResults
	}

	if (!renderResults?.[0]?.image) return [] // TODO: TEMP

	const result = [] as {
		layer: string,
		image: Image,
		angle?: number,
		sx?: number,
		sy?: number,
		sw?: number,
		sh?: number,
		pos: { x: number, y: number },
		drawPosOff: { x: number, y: number },
		off: { x: number, y: number },
		size: { w: number, h: number },
		baseSize?: { w: number, h: number }
	}[]

	const buildInfo = item.buildInfo[findBuildInfoIndex(item, hullDirection)]

	for (const renderResult of renderResults) {
		const { image, textureSizeOff, texturePosOff, baseBoundOff, sx, sy, sw, sh } = renderResult
		const bounds = Object.assign({}, buildInfo.bounds)

		if (baseBoundOff) {
			bounds.x += baseBoundOff.x
			bounds.y += baseBoundOff.y
		}
		const baseSize = {
			w: bounds.x * squareSize,
			h: bounds.y * squareSize
		}

		const sizeOff = {
			x: (bounds.x / 2) * squareSize,
			y: (bounds.y / 2) * squareSize
		}

		const textureSizePosOff = { x: 0, y: 0 }
		if (textureSizeOff) {
			const x = textureSizeOff.x / 2 * squareSize
			const y = textureSizeOff.y / 2 * squareSize
			const right = !hullDirection || hullDirection == FixedAngle.RIGHT
			if (right || hullDirection == FixedAngle.LEFT) {
				sizeOff.x += x
				sizeOff.y += y
				textureSizePosOff.x += x * (right ? 1 : -1)
				textureSizePosOff.y += y * (right ? 1 : -1)
			} else {
				const up = hullDirection == FixedAngle.UP
				sizeOff.x += y
				sizeOff.y += x
				textureSizePosOff.x -= y * (up ? 1 : -1)
				textureSizePosOff.y -= x * (up ? 1 : -1)
			}
		}

		const drawPosOff = {
			x: squareSize / 2 - sizeOff.x + textureSizePosOff.x,
			y: squareSize / 2 - sizeOff.y + textureSizePosOff.y
		}

		const hullDirR = !hullDirection || hullDirection == FixedAngle.RIGHT
		const hullDirH = hullDirR || hullDirection == FixedAngle.LEFT

		if (texturePosOff) {
			if (hullDirH) {
				drawPosOff.x += (texturePosOff.x * squareSize) * (hullDirR ? 1 : -1)
				drawPosOff.y += (texturePosOff.y * squareSize) * (hullDirR ? 1 : -1)
			} else {
				const hullDirUp = hullDirection == FixedAngle.UP
				drawPosOff.x -= (texturePosOff.y * squareSize) * (hullDirUp ? 1 : -1)
				drawPosOff.y -= (texturePosOff.x * squareSize) * (hullDirUp ? 1 : -1)
			}
		}

		const pos = {
			x: x * squareSize,
			y: (main.height - 1 - y) * squareSize
		}

		const size = {
			w: sizeOff.x * 2,
			h: sizeOff.y * 2
		}

		const angle =
			config?.angle != null && buildInfo.build_angle == "Any"
				? -config?.angle
				: config?.fixedAngle != null && buildInfo.build_angle == "Fixed"
					? -config.fixedAngle.enumValue * 90
					: undefined

		result.push({ layer: renderResult.layer, image, angle, sx, sy, sw, sh, pos, drawPosOff, off: sizeOff, size, baseSize })
	}
	return result
}

export function findBuildInfoIndex(item: Item, hullDirection: FixedAngle) {
	const i = item.buildInfo.findIndex(bi =>
		(hullDirection == FixedAngle.UP || hullDirection == FixedAngle.DOWN) && bi.buildDirection == "HORIZONTAL"
		|| (hullDirection == FixedAngle.RIGHT || hullDirection == FixedAngle.LEFT) && bi.buildDirection == "VERTICAL"
		|| hullDirection == null && bi.build_angle
	)
	return i == -1 ? (item.buildInfo[1] ? 1 : 0) : i
}
